import type { ApiResponse } from "@entrilia/shared/api/types.ts";
import DialogHeader from "@entrilia/shared/components/dialog/DialogHeader.tsx";
import PromptDialog from "@entrilia/shared/components/dialog/PromptDialog.tsx";
import DialogButtons from "@entrilia/shared/components/DialogButtons.tsx";
import useRequest from "@entrilia/shared/hooks/useRequest.ts";
import DeleteDialogIcon from "@entrilia/shared/icons/DeleteDialogIcon.tsx";
import { Alert, Button, Stack, Typography } from "@mui/material";
import { useCallback } from "react";
import type { DeleteReportItem } from "../../../../../../api/types/svTypes.ts";
import type { FromReport } from "./FromReport.ts";

interface Props {
  reports: FromReport[];
  doDelete: (reports: DeleteReportItem[]) => Promise<ApiResponse<unknown>>;
  onDeleted?: () => void;
  onClose: () => void;
}

export default function DeleteReport({ reports, doDelete, onClose, onDeleted }: Props) {
  const deleteReports = useCallback(
    () => doDelete(reports.map((r) => ({ clientCode: r.clientCode, reportId: r.reportId }))),
    [doDelete, reports]
  );

  const { request, error, isLoading: deleting } = useRequest(deleteReports);

  const handleDeleteReports = async () => {
    const [, error] = await request();
    if (!error) {
      onDeleted?.();
      onClose();
    }
  };

  const isSingleReport = reports.length === 1;

  return (
    <PromptDialog open={true} sx={{ width: 514, minHeight: 305 }}>
      <DialogHeader text="Delete Report" disabled={deleting} onClose={onClose} />
      {error && (
        <Alert sx={{ mb: ".5rem" }} severity="error">
          An error occurred while deleting the report.
        </Alert>
      )}
      <Stack direction="column" useFlexGap spacing={3} sx={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
        <DeleteDialogIcon sx={{ width: 80, height: 80 }} />
        <Stack useFlexGap spacing={1} sx={{ alignItems: "center" }}>
          <Typography variant="h6" color="textPrimary" sx={{ fontFamily: "Roboto", textAlign: "center" }}>
            {isSingleReport ? `Delete ${reports?.[0]?.name}` : `Delete ${reports.length} reports`}
          </Typography>
          <Typography sx={{ color: "text.secondary" }}>
            {isSingleReport
              ? "Are you certain you want to delete the report?"
              : "Are you certain you want to delete these reports?"}
          </Typography>
        </Stack>
      </Stack>
      <DialogButtons onCancel={onClose}>
        <Button variant="outlined" color="error" disabled={deleting} loading={deleting} onClick={handleDeleteReports}>
          Delete
        </Button>
      </DialogButtons>
    </PromptDialog>
  );
}
